import moment from 'moment'
import { ActionCode, IReportAction } from './report-action.model'

// Task-style display names for the system-generated report actions. The stored titles are
// entity-first ('London Distribution Centre, Electricity', 'Vehicle V12'), which reads poorly
// in task lists that already have an Entity column, so these lead with what the user has to do.
const ACTION_TITLES: Record<ActionCode, string> = {
  [ActionCode.LocationElectricity]: 'Enter electricity usage',
  [ActionCode.LocationGas]: 'Enter gas usage',
  [ActionCode.LocationSteam]: 'Enter heat and steam usage',
  [ActionCode.LocationAircon]: 'Enter air conditioning data',
  [ActionCode.LocationWater]: 'Enter water usage',
  [ActionCode.LocationWaste]: 'Enter waste collection data',
  [ActionCode.LocationUtilities]: 'Enter utilities data',
  [ActionCode.Vehicle]: 'Enter vehicle mileage and fuel',
}

type TitledAction = Pick<IReportAction, 'title' | 'actionCode' | 'location' | 'vehicle'>

// Only rewrite titles that still match the backend generator's template; anything else
// (curated or hand-edited titles) is deliberate wording and must be shown as stored.
const isGeneratedTitle = (action: TitledAction): boolean => {
  if (action.actionCode === ActionCode.Vehicle) return action.title.startsWith('Vehicle ')
  const siteName = action.location?.friendlyName
  return !!siteName && action.title.startsWith(`${siteName},`)
}

export const friendlyActionTitle = (action: TitledAction): string => {
  const niceTitle = ACTION_TITLES[action.actionCode]
  return niceTitle && isGeneratedTitle(action) ? niceTitle : action.title
}

// Entity label for a task row: the site for location actions, the vehicle otherwise.
export const actionEntityName = (action: Pick<IReportAction, 'location' | 'vehicle'>): string => {
  if (action.location?.friendlyName) return action.location.friendlyName
  if (action.vehicle?.customUniqueId) return `Vehicle ${action.vehicle.customUniqueId}`
  return ''
}

// Period label for an action row ('Jan 2026', or '2026' / '2025 - 2026' for annual
// actions). Shared by the Actions panel and the dashboard's Needs attention block so
// the two always describe an action's period the same way.
export const actionPeriodLabel = (
  action: Pick<IReportAction, 'actionBillingPeriod' | 'actionPeriod' | 'actionYear' | 'startDate' | 'endDate'>,
): string => {
  if (action.actionBillingPeriod === 'annually') {
    const startYear = moment(action.startDate).year()
    const endYear = moment(action.endDate).year()
    return startYear === endYear ? `${startYear}` : `${startYear} - ${endYear}`
  }
  return `${action.actionPeriod} ${action.actionYear}`
}
